import { create } from 'zustand';
import api from '../lib/api';

export enum UserRole {
  OWNER    = 'OWNER',
  DIRECTOR = 'DIRECTOR',
  MANAGER  = 'MANAGER',
  OPERATOR = 'OPERATOR',
  VIEWER   = 'VIEWER',
  DRIVER   = 'DRIVER',
}

export function hasRole(user: User | null, ...roles: UserRole[]): boolean {
  if (!user) return false;
  return roles.includes(user.role as UserRole);
}

const resetMiningState = async () => {
  const { useMiningStore } = await import('./miningStore');
  useMiningStore.getState().resetMiningState();
};

const resetMiningStateIfTenantChanged = (previousUser: User | null, nextUser: User | null) => {
  if (previousUser?.id !== nextUser?.id || previousUser?.tenantId !== nextUser?.tenantId) {
    void resetMiningState();
  }
};

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  phone?: string;
  tenantId?: string;
  avatarUrl?: string;
  needsOnboarding?: boolean;
  productTourSeenAt?: string | null;
  hasPassword?: boolean;
  createdAt?: string;
  sector?: string; // primary sector selected during onboarding (mining, logistics, agriculture, construction, power)
  assignedSiteIds?: string[] | null; // DIRECTOR/MANAGER: mine sites this user manages
  aiCompanionSkin?: string;
  aiCompanionName?: string;
  aiCompanionConfiguredAt?: string | null;
  isDemo?: boolean;
}

export type AuthResponse = User & {
  accessToken?: string;
  mfaRequired?: boolean;
  mfaEnrolmentRequired?: boolean;
  mfaTicket?: string;
};

export function userFromAuthPayload(data: AuthResponse): User {
  const user = { ...data };
  delete user.accessToken;
  delete user.mfaRequired;
  delete user.mfaEnrolmentRequired;
  delete user.mfaTicket;
  return user;
}

export interface MfaEnrolmentBundle {
  enrolmentId: string;
  otpauthUrl: string;
  manualEntryKey: string;
  recoveryCodes: string[];
}

export interface MfaStatusPayload {
  enrolled: boolean;
  confirmed: boolean;
  confirmedAt: string | null;
  lastVerifiedAt: string | null;
  remainingRecoveryCodes: number;
  required: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string, rememberSession?: boolean) => Promise<AuthResponse>;
  register: (data: { email: string; password: string; firstName: string; lastName: string; companyName?: string; industry?: string }) => Promise<void>;
  logout: () => void;
  loadFromStorage: () => void;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    aiCompanionSkin?: string;
    aiCompanionName?: string;
  }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  setPassword: (newPassword: string) => Promise<void>;

  // Pre-login MFA flow — called after login() returns mfaRequired / mfaEnrolmentRequired
  verifyMfaLogin: (mfaTicket: string, code: string, rememberSession?: boolean) => Promise<AuthResponse>;
  beginMfaEnrolmentLogin: (mfaTicket: string) => Promise<MfaEnrolmentBundle & { mfaTicket?: string }>;
  confirmMfaEnrolmentLogin: (mfaTicket: string, code: string, rememberSession?: boolean) => Promise<AuthResponse>;

  // Authenticated MFA management — called from Profile → Security
  fetchMfaStatus: () => Promise<MfaStatusPayload>;
  beginMfaEnrolment: (currentPassword: string) => Promise<MfaEnrolmentBundle>;
  confirmMfaEnrolment: (code: string) => Promise<MfaStatusPayload>;
  disableMfa: (currentPassword: string, code: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,

  login: async (email, password, rememberSession = false) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post<AuthResponse>('/auth/login', { email, password, rememberSession });
      // MFA gate: caller must continue with verifyMfaLogin / enrol flow.
      // Do NOT persist token; data.accessToken is empty in this branch.
      if (data?.mfaRequired || data?.mfaEnrolmentRequired) {
        set({ isLoading: false });
        return data;
      }
      if (!data.accessToken) {
        throw new Error('Login response did not include an access token');
      }
      const user = userFromAuthPayload(data);
      const previousUser = get().user;
      resetMiningStateIfTenantChanged(previousUser, user);
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('user', JSON.stringify(user));
      set({
        user,
        token: data.accessToken,
        isAuthenticated: true,
        isLoading: false,
      });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  verifyMfaLogin: async (mfaTicket, code, rememberSession = false) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post<AuthResponse>('/auth/login/mfa/verify', { mfaTicket, code, rememberSession });
      if (!data.accessToken) {
        throw new Error('MFA response did not include an access token');
      }
      const user = userFromAuthPayload(data);
      const previousUser = get().user;
      resetMiningStateIfTenantChanged(previousUser, user);
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('user', JSON.stringify(user));
      set({ user, token: data.accessToken, isAuthenticated: true, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  beginMfaEnrolmentLogin: async (mfaTicket) => {
    const { data } = await api.post('/auth/login/mfa/enrol', { mfaTicket });
    return data;
  },

  confirmMfaEnrolmentLogin: async (mfaTicket, code, rememberSession = false) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post<AuthResponse>('/auth/login/mfa/confirm', { mfaTicket, code, rememberSession });
      if (!data.accessToken) {
        throw new Error('MFA enrolment response did not include an access token');
      }
      const user = userFromAuthPayload(data);
      const previousUser = get().user;
      resetMiningStateIfTenantChanged(previousUser, user);
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('user', JSON.stringify(user));
      set({ user, token: data.accessToken, isAuthenticated: true, isLoading: false });
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  fetchMfaStatus: async () => {
    const { data } = await api.get('/mfa/status');
    return data;
  },

  beginMfaEnrolment: async (currentPassword) => {
    const { data } = await api.post('/mfa/enrol/begin', { currentPassword });
    return data;
  },

  confirmMfaEnrolment: async (code) => {
    const { data } = await api.post('/mfa/enrol/confirm', { code });
    return data;
  },

  disableMfa: async (currentPassword, code) => {
    await api.post('/mfa/disable', { currentPassword, code });
  },

  register: async (formData) => {
    set({ isLoading: true });
    try {
      await api.post('/auth/register', formData);
      set({ isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  logout: () => {
    void resetMiningState();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    set({ user: null, token: null, isAuthenticated: false });
  },

  loadFromStorage: () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      try {
        const user = userFromAuthPayload(JSON.parse(userStr));
        const previousUser = get().user;
        resetMiningStateIfTenantChanged(previousUser, user);
        localStorage.setItem('user', JSON.stringify(user));
        set({ user, token, isAuthenticated: true });
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  },

  fetchProfile: async () => {
    try {
      const { data } = await api.get('/auth/me');
      const user = userFromAuthPayload(data);
      const previousUser = get().user;
      resetMiningStateIfTenantChanged(previousUser, user);
      set({ user, isAuthenticated: true });
      localStorage.setItem('user', JSON.stringify(user));
    } catch (err: unknown) {
      // Only logout on 401 (invalid/expired token). Don't logout on network errors.
      const e = err as { response?: { status?: number } };
      if (e?.response?.status === 401) {
        get().logout();
      }
    }
  },

  updateProfile: async (profileData) => {
    const { data } = await api.patch('/auth/profile', profileData);
    const user = userFromAuthPayload(data);
    set({ user });
    localStorage.setItem('user', JSON.stringify(user));
  },

  changePassword: async (currentPassword, newPassword) => {
    await api.post('/auth/change-password', { currentPassword, newPassword });
  },

  setPassword: async (newPassword) => {
    await api.post('/auth/set-password', { newPassword });
    const user = get().user;
    if (user) {
      const updatedUser = userFromAuthPayload({ ...user, hasPassword: true });
      set({ user: updatedUser });
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  },
}));
